import { BadgeProps, EmptyStateProps } from '@silverfort/sf-components';

export type TabValue = string | number;

export type EmptyStateConfig = Partial<EmptyStateProps>;

export interface PreviewItem {
    name: string;
    hits: number;
    guid: string;
    badgeProps?: BadgeProps;
}

export interface FullItem extends PreviewItem {
    lastAuthTime: Date;
    riskLevel: number;
    domain: string;
    badgeProps?: BadgeProps;
    [key: string]: unknown;
}

export interface TabConfig<T extends TabValue = TabValue> {
    value: T;
    name: string;
    previewData: PreviewItem[];
    fullData: FullItem[];
    previewLoading: boolean;
    fullLoading: boolean;
    disabled?: boolean;
    emptyStateConfig?: EmptyStateConfig;
}

export interface SingleDataConfig {
    data: FullItem[];
    loading: boolean;
}

interface BaseTopMetricsChartProps {
    label: string;
    onShowMore?: () => void;
}

interface WithTabsProps<T extends TabValue = TabValue> extends BaseTopMetricsChartProps {
    tabs: TabConfig<T>[];
    selectedTab: T;
    onTabChange: (tab: T) => void;
    onInvestigationClick?: (guid: string, tabValue: T) => void;
}

interface WithSingleDataProps extends BaseTopMetricsChartProps {
    tabs?: never;
    selectedTab?: never;
    onTabChange?: never;
    onInvestigationClick?: (guid: string) => void;
    data: PreviewItem[];
    fullData: FullItem[];
    loading: boolean;
    fullLoading: boolean;
    disabled?: boolean;
    emptyStateConfig?: EmptyStateConfig;
}

export type TopMetricsChartProps<T extends TabValue = TabValue> = WithTabsProps<T> | WithSingleDataProps;

export interface BaseTabsProps<T extends TabValue = TabValue> {
    selectedTab: T;
    tabs: TabConfig<T>[];
}

export interface BaseSingleDataProps {
    singleData: SingleDataConfig;
}

export interface BaseTabsVariant<T extends TabValue = TabValue> extends BaseTabsProps<T> {
    singleData?: never;
}

export interface BaseSingleDataVariant extends BaseSingleDataProps {
    selectedTab?: never;
    tabs?: never;
}

export interface BaseTopMetricLayoutProps {
    children: React.ReactNode;
    label: string;
    onShowMore: () => void;
    disabled?: boolean;
}

export interface TopMetricLayoutWithTabsProps<T extends TabValue = TabValue>
    extends BaseTopMetricLayoutProps,
        BaseTabsVariant<T> {
    onTabChange: (tab: T) => void;
}

export interface TopMetricLayoutWithoutTabsProps extends BaseTopMetricLayoutProps {
    tabs?: never;
    selectedTab?: never;
    onTabChange?: never;
}

export type TopMetricLayoutProps<T extends TabValue = TabValue> =
    | TopMetricLayoutWithTabsProps<T>
    | TopMetricLayoutWithoutTabsProps;

export const hasTabsProps = <T extends TabValue>(
    props: TopMetricLayoutProps<T>
): props is TopMetricLayoutWithTabsProps<T> => {
    return 'tabs' in props && 'selectedTab' in props && 'onTabChange' in props;
};

export interface BaseTopMetricPopupProps {
    open: boolean;
    onClose: () => void;
    title: string;
}

export interface TopMetricPopupWithTabsProps<T extends TabValue = TabValue>
    extends BaseTopMetricPopupProps,
        BaseTabsVariant<T> {}

export interface TopMetricPopupWithSingleDataProps extends BaseTopMetricPopupProps, BaseSingleDataVariant {}

export type TopMetricPopupProps<T extends TabValue = TabValue> =
    | TopMetricPopupWithTabsProps<T>
    | TopMetricPopupWithSingleDataProps;

export const hasPopupTabsProps = <T extends TabValue>(
    props: TopMetricPopupProps<T>
): props is TopMetricPopupWithTabsProps<T> => {
    return 'tabs' in props && 'selectedTab' in props && !('singleData' in props);
};

export const hasPopupSingleDataProps = <T extends TabValue>(
    props: TopMetricPopupProps<T>
): props is TopMetricPopupWithSingleDataProps => {
    return 'singleData' in props && !('tabs' in props);
};

export type TopMetricPopupContentProps<T extends TabValue = TabValue> = BaseTabsVariant<T> | BaseSingleDataVariant;

export const hasContentTabsProps = <T extends TabValue>(
    props: TopMetricPopupContentProps<T>
): props is BaseTabsVariant<T> => {
    return 'tabs' in props && 'selectedTab' in props && !('singleData' in props);
};

export const hasContentSingleDataProps = <T extends TabValue>(
    props: TopMetricPopupContentProps<T>
): props is BaseSingleDataVariant => {
    return 'singleData' in props && !('tabs' in props);
};
